import React from "react";
import { ManualEntity, DropdownOption } from "./types";
import { CustomDropdown } from "../CustomDropdown";

interface EntitySectionProps {
    title: string;
    entityType: 'vendor' | 'consignee' | 'purchaser';
    entityId: string;
    manualEntity: ManualEntity;
    useManualEntry: boolean;
    dropdownOptions: DropdownOption[];
    onToggleManualEntry: (entityType: 'vendor' | 'consignee' | 'purchaser') => void;
    onDropdownChange: (name: string, value: string) => void;
    onManualEntityChange: (
        entity: 'vendor' | 'consignee' | 'purchaser',
        field: 'name' | 'address' | 'city' | 'stateProvince' | 'postalCode' | 'country',
        value: string
    ) => void;
}

export const EntitySection: React.FC<EntitySectionProps> = ({
    title,
    entityType,
    entityId,
    manualEntity,
    useManualEntry,
    dropdownOptions,
    onToggleManualEntry,
    onDropdownChange,
    onManualEntityChange
}) => {
    const idFieldName = `${entityType}Id`;

    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {title} *
                </label>
                <div className="flex items-center">
                    <span className="text-xs mr-2">Manual Entry</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={useManualEntry}
                            onChange={() => onToggleManualEntry(entityType)}
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                </div>
            </div>

            {useManualEntry ? (
                <div className="space-y-2">
                    <input
                        type="text"
                        placeholder={`${title} Name`}
                        value={manualEntity.name}
                        onChange={(e) => onManualEntityChange(entityType, 'name', e.target.value)}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        required
                    />
                    <input
                        type="text"
                        placeholder="Street Address"
                        value={manualEntity.address}
                        onChange={(e) => onManualEntityChange(entityType, 'address', e.target.value)}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        required
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="text"
                            placeholder="City"
                            value={manualEntity.city}
                            onChange={(e) => onManualEntityChange(entityType, 'city', e.target.value)}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            required
                        />
                        <input
                            type="text"
                            placeholder="State/Province"
                            value={manualEntity.stateProvince}
                            onChange={(e) => onManualEntityChange(entityType, 'stateProvince', e.target.value)}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="text"
                            placeholder="Postal/Zip Code"
                            value={manualEntity.postalCode}
                            onChange={(e) => onManualEntityChange(entityType, 'postalCode', e.target.value)}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            required
                        />
                        <input
                            type="text"
                            placeholder="Country"
                            value={manualEntity.country}
                            onChange={(e) => onManualEntityChange(entityType, 'country', e.target.value)}
                            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                            required
                        />
                    </div>
                </div>
            ) : (
                <CustomDropdown
                    options={dropdownOptions}
                    value={entityId}
                    onChange={(value) => onDropdownChange(idFieldName, value)}
                    placeholder={`Select ${title}`}
                    required={!useManualEntry}
                />
            )}
        </div>
    );
};
